import { useEffect, useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, ExternalLink, Bell } from "lucide-react";
import { adminFetch } from "./adminApi";

const CATEGORIES = ["banking", "ssc", "railway", "mba", "insurance", "others"];

const EMPTY = {
  title: "", exam_name: "", category: "banking", course_slug: "", vacancies: "",
  posted_date: "", last_date: "", exam_date: "", apply_url: "", source_url: "",
  eligibility_short: "", summary: "", salary: "", application_fee: "",
  eligibility_full: "", selection_process: "", how_to_apply: "", details: "", is_active: true,
};

const Input = ({ label, ...props }) => (
  <div>
    <label className="block text-xs font-semibold text-vedanti-navy mb-1">{label}</label>
    <input
      {...props}
      className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-vedanti-orange/40 text-sm"
    />
  </div>
);

const Area = ({ label, hint, ...props }) => (
  <div>
    <label className="block text-xs font-semibold text-vedanti-navy mb-1">{label}</label>
    <textarea
      {...props}
      className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-vedanti-orange/40 text-sm min-h-[90px]"
    />
    {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
  </div>
);

export default function AdminNotifications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = closed, {} = new, {...} = edit
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminFetch("/admin/notifications");
      setItems(data);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditing({}); };
  const openEdit = (n) => { setForm({ ...EMPTY, ...n }); setEditing(n); };
  const close = () => setEditing(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.title || !form.exam_name) { toast.error("Title and Exam Name are required"); return; }
    setSaving(true);
    try {
      if (editing.id) {
        await adminFetch(`/admin/notifications/${editing.id}`, { method: "PUT", body: JSON.stringify(form) });
        toast.success("Notification updated");
      } else {
        await adminFetch("/admin/notifications", { method: "POST", body: JSON.stringify(form) });
        toast.success("Notification created");
      }
      close();
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (n) => {
    if (!window.confirm(`Delete "${n.title}"? This cannot be undone.`)) return;
    try {
      await adminFetch(`/admin/notifications/${n.id}`, { method: "DELETE" });
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div data-testid="admin-notifications-page">
      <Helmet><title>Manage Notifications | Vedanti Admin</title></Helmet>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-vedanti-navy flex items-center gap-2">
            <Bell size={22} className="text-vedanti-orange" /> Job Notifications
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">{items.length} total · manage exam alerts &amp; their detail pages</p>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-2 bg-vedanti-orange hover:bg-orange-600 text-white font-bold text-sm px-4 py-2.5 rounded-lg transition-all" data-testid="add-notification-btn">
          <Plus size={16} /> Add Notification
        </button>
      </div>

      {loading ? (
        <div className="py-20 text-center text-slate-400">Loading…</div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Title</th>
                <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Category</th>
                <th className="text-left px-4 py-3 font-semibold hidden lg:table-cell">Last Date</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-right px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((n) => (
                <tr key={n.id} className="hover:bg-slate-50" data-testid={`admin-notif-row-${n.id}`}>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-vedanti-navy line-clamp-1">{n.title}</p>
                    <a href={`/notifications/${n.slug || n.id}`} target="_blank" rel="noopener noreferrer nofollow" className="text-[11px] text-vedanti-orange inline-flex items-center gap-1">
                      /notifications/{n.slug || n.id} <ExternalLink size={10} />
                    </a>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell uppercase text-xs font-bold text-slate-500">{n.category}</td>
                  <td className="px-4 py-3 hidden lg:table-cell text-slate-500">{n.last_date || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${n.is_active ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-500"}`}>
                      {n.is_active ? "Active" : "Expired"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(n)} className="text-slate-500 hover:text-vedanti-navy p-1.5" data-testid={`edit-notif-${n.id}`}><Pencil size={15} /></button>
                    <button onClick={() => remove(n)} className="text-slate-400 hover:text-red-500 p-1.5" data-testid={`delete-notif-${n.id}`}><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Drawer form */}
      {editing && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="notif-form-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={close} />
          <div className="relative w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="font-heading font-bold text-vedanti-navy">{editing.id ? "Edit" : "New"} Notification</h2>
              <button onClick={close} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <Input label="Title *" value={form.title} onChange={set("title")} data-testid="notif-field-title" />
              <div className="grid grid-cols-2 gap-4">
                <Input label="Exam Name *" value={form.exam_name} onChange={set("exam_name")} data-testid="notif-field-exam" />
                <div>
                  <label className="block text-xs font-semibold text-vedanti-navy mb-1">Category</label>
                  <select value={form.category} onChange={set("category")} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-vedanti-orange/40" data-testid="notif-field-category">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c.toUpperCase()}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input label="Vacancies" value={form.vacancies} onChange={set("vacancies")} />
                <Input label="Course Slug (links to coaching page)" value={form.course_slug} onChange={set("course_slug")} placeholder="e.g. ssc-cgl" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <Input label="Posted Date" type="date" value={form.posted_date} onChange={set("posted_date")} />
                <Input label="Last Date" type="date" value={form.last_date} onChange={set("last_date")} />
                <Input label="Exam Date" value={form.exam_date} onChange={set("exam_date")} placeholder="YYYY-MM-DD or text" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input label="Eligibility (short)" value={form.eligibility_short} onChange={set("eligibility_short")} placeholder="Graduate · 18-30 yrs" />
                <Input label="Salary" value={form.salary} onChange={set("salary")} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input label="Apply URL" value={form.apply_url} onChange={set("apply_url")} placeholder="https://" />
                <Input label="Source URL" value={form.source_url} onChange={set("source_url")} placeholder="https://" />
              </div>
              <Area label="Summary (card preview)" value={form.summary} onChange={set("summary")} />
              <div className="pt-2 border-t border-slate-100">
                <p className="text-xs font-bold uppercase text-slate-400 tracking-wider mb-3">Detail Page Content</p>
                <div className="space-y-4">
                  <Area label="Details / Overview" value={form.details} onChange={set("details")} hint="Supports basic HTML (<p>, <h3>, <ul><li>, <strong>)." />
                  <Input label="Application Fee" value={form.application_fee} onChange={set("application_fee")} />
                  <Area label="Full Eligibility" value={form.eligibility_full} onChange={set("eligibility_full")} />
                  <Area label="Selection Process" value={form.selection_process} onChange={set("selection_process")} />
                  <Area label="How to Apply" value={form.how_to_apply} onChange={set("how_to_apply")} />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm font-semibold text-vedanti-navy">
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} className="w-4 h-4 accent-vedanti-orange" data-testid="notif-field-active" />
                Show as active (also auto-expires after Last Date)
              </label>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-slate-100 px-6 py-4 flex justify-end gap-3">
              <button onClick={close} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100">Cancel</button>
              <button onClick={save} disabled={saving} className="px-5 py-2 rounded-lg text-sm font-bold bg-vedanti-orange hover:bg-orange-600 text-white disabled:opacity-60" data-testid="notif-save-btn">
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
